import { useDependency } from 'ditox-react';
import { uniq } from 'rambda';
import { useCallback } from 'react';
import type { ConversationFormDataType } from '@/components/conversations/conversation-form';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import useUploadFile from '@/hooks/files/use-upload-file';
import useUserInfo from '@/hooks/users/use-user-info';
import {
  ConversationMessageContentType,
  type ConversationMessageTypeUnion,
  transformConversationMessageToSendMessage,
  transformSendMessageToConversationMessage,
} from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import type { DispatchEvent } from '@/models/events/dispatch-event';
import {
  type SendChatMessageType,
  type SendGroupChatMessageType,
  SendMessageCommand,
} from '@/models/messages/send-message';
import { UserType } from '@/models/user/user-info';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';
import { readableFileSize } from '@/utils/file-size';
import { FileType } from '@/utils/file-type';
import { convertUploadableToDataURL, convertUrlToUploadable } from '@/utils/uploadable';
import { useFriendBySessionId } from '../contacts/use-friends';
import useFriendInfo from '../users/use-friend-info';

export default function useMessageWebSocketDispatch(type: ConversationType, id: number | null | undefined) {
  const auth = useAuthStore((s) => s.auth);
  const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);
  const { data: userInfo } = useUserInfo();

  const { friend } = useFriendBySessionId(id);
  const { friendInfo } = useFriendInfo(friend?.FriendID);
  const isAdminSession = friendInfo?.UserInfo?.Type === UserType.Admin;

  const { upload, isLoading: isUploadingFile } = useUploadFile((isAdminSession && id) || undefined);

  const retry = (message: ConversationMessageTypeUnion) => {
    if (message.Status !== null || Date.now() - message.TimeStamp < 5000) return;

    const newMessage = useMessageStore.getState().unmarkStale(message);
    const sendMessage = transformConversationMessageToSendMessage(newMessage);

    if (sendMessage) {
      return handleSendMessage(sendMessage);
    }
  };

  const handleSendMessage = async (sendMessage: SendChatMessageType | SendGroupChatMessageType, isResend = false) => {
    const message = sendMessage;

    if (!isResend) await useMessageStore.getState().add(transformSendMessageToConversationMessage(message, userInfo));

    const [content, ...rest] = message.Message.split('|');
    if (!content) return Promise.reject();

    if (content.startsWith('data:') && rest.length >= 3) {
      const [attachmentType, name, size, ...extra] = rest;
      const attachment = convertUrlToUploadable(content, attachmentType);
      if (name) attachment.name = name;
      if (size) attachment.size = Number.parseInt(size);

      switch (message.MsgType) {
        case ConversationMessageContentType.Audio:
          {
            attachment.compress = true;
            const { FileUrl } = await upload(attachment);
            message.Message = [FileUrl, ...extra].join('|');
          }
          break;
        case ConversationMessageContentType.Image:
          {
            // attachment.compress = true;
            const { FileUrl } = await upload(attachment);
            message.Message = FileUrl;
          }
          break;
        case ConversationMessageContentType.Video:
          {
            const { FileUrl } = await upload(attachment);
            message.Message = FileUrl;
          }
          break;
        case ConversationMessageContentType.File:
          {
            const { FileUrl } = await upload(attachment);
            message.Message = [FileUrl, name, readableFileSize(attachment.size)].join('|');
          }
          break;
      }

      await useMessageStore.getState().add(transformSendMessageToConversationMessage(message, userInfo));
    }

    webSocketManager.dispatch({
      action: 'send',
      params: { message, isResend },
    });
  };

  const submit = useCallback(
    async (data: ConversationFormDataType) => {
      if (!auth || !id) return Promise.reject();

      const message: SendChatMessageType | SendGroupChatMessageType =
        type === 'session'
          ? {
              Cmd: SendMessageCommand.Chat,
              Message: data.Content ?? '',
              UserID: auth.UserID,
              ChatSessionID: id,
              QuoteChatID: data.Quote?.ID,
              MsgType: ConversationMessageContentType.Text,
              TimeStamp: Date.now(),
            }
          : {
              Cmd: SendMessageCommand.GroupChat,
              Message: data.Content ?? '',
              UserID: auth.UserID,
              GroupID: id,
              QuoteGroupChatID: data.Quote?.ID,
              MsgType: ConversationMessageContentType.Text,
              TimeStamp: Date.now(),
              AtIDs: data.AtIDs ? uniq(data.AtIDs).join('|') : null,
            };

      if ('ContentType' in data) {
        switch (data.ContentType) {
          case ConversationMessageContentType.Emoticon:
          case ConversationMessageContentType.Contact:
            message.MsgType = data.ContentType;
            break;
        }
      }

      if ('Attachment' in data && data.Attachment) {
        for (let i = 0; i < data.Attachment.length; i++) {
          const attachment = data.Attachment[i];
          if (!attachment) return;

          const { name, size, type } = attachment;
          const dataUrl = await convertUploadableToDataURL(attachment);
          message.Message = [dataUrl, type, name, size].join('|');

          switch (data.AttachmentType) {
            case FileType.Image: {
              message.MsgType = ConversationMessageContentType.Image;
              break;
            }
            case FileType.Video: {
              message.MsgType = ConversationMessageContentType.Video;
              break;
            }
            case FileType.Audio: {
              message.Message += `|${data.Content}`;
              message.MsgType = ConversationMessageContentType.Audio;
              break;
            }
            case FileType.File: {
              message.MsgType = ConversationMessageContentType.File;
              break;
            }
            default:
              message.MsgType = ConversationMessageContentType.Text;
              break;
          }

          message.TimeStamp += i;

          await handleSendMessage(message);
        }
      } else {
        await handleSendMessage(message);
      }
    },

    [auth, id, type],
  );

  return {
    submit,
    retry,
    dispatch: (event: DispatchEvent) => webSocketManager.dispatch(event),
    reconnect: () => webSocketManager.reconnect(),
    isLoading: isUploadingFile,
  };
}
